(() => {
  const normalizeText = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('pt-BR')
      .trim();

  const parseCoordinate = (value, minimum, maximum) => {
    const coordinate = Number.parseFloat(String(value || '').replace(',', '.'));
    return Number.isFinite(coordinate) && coordinate >= minimum && coordinate <= maximum ? coordinate : null;
  };

  const haversineDistance = (origin, destination) => {
    const earthRadiusKm = 6371;
    const toRadians = (degrees) => (degrees * Math.PI) / 180;
    const latitudeDelta = toRadians(destination.latitude - origin.latitude);
    const longitudeDelta = toRadians(destination.longitude - origin.longitude);
    const originLatitude = toRadians(origin.latitude);
    const destinationLatitude = toRadians(destination.latitude);
    const a =
      Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(originLatitude) * Math.cos(destinationLatitude) * Math.sin(longitudeDelta / 2) ** 2;

    return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.searchInput = this.querySelector('[data-store-search]');
      this.list = this.querySelector('[data-store-list]');
      this.noResults = this.querySelector('[data-no-results]');
      this.resultsCount = this.querySelector('[data-results-count]');
      this.mapElement = this.querySelector('[data-store-map]');
      this.mapFallback = this.querySelector('[data-map-fallback]');
      this.mapFallbackMessage = this.querySelector('[data-map-fallback-message]');
      this.locationButton = this.querySelector('[data-location-button]');
      this.locationLabel = this.querySelector('[data-location-label]');
      this.locationStatus = this.querySelector('[data-location-status]');
      this.defaultZoom = Number.parseInt(this.dataset.defaultZoom, 10) || 13;
      this.enableLocation = this.dataset.enableLocation === 'true';
      this.requestLocationOnLoad = this.dataset.requestLocationOnLoad === 'true';
      this.showDistance = this.dataset.showDistance === 'true';
      this.locationRequested = false;
      this.customerLocation = null;
      this.markers = new Map();

      this.stores = Array.from(this.querySelectorAll('[data-store-item]')).map((element, index) => {
        const latitude = parseCoordinate(element.dataset.storeLatitude, -90, 90);
        const longitude = parseCoordinate(element.dataset.storeLongitude, -180, 180);
        const button = element.querySelector('[data-store-button]');
        const distanceElement = element.querySelector('[data-store-distance]');
        const unmappedElement = element.querySelector('[data-store-unmapped]');
        const hasCoordinates = latitude !== null && longitude !== null;

        if (!hasCoordinates && button) {
          button.disabled = true;
          button.setAttribute('aria-label', `${element.dataset.storeName}. Localização indisponível no mapa.`);
          if (unmappedElement) unmappedElement.hidden = false;
        }

        return {
          id: element.dataset.storeId || String(index),
          name: element.dataset.storeName || 'Ponto de venda',
          address: element.dataset.storeAddress || 'Endereço não informado',
          city: element.dataset.storeCity || '',
          searchText: normalizeText(
            `${element.dataset.storeName || ''} ${element.dataset.storeAddress || ''} ${element.dataset.storeCity || ''}`
          ),
          latitude,
          longitude,
          hasCoordinates,
          distance: null,
          originalIndex: index,
          element,
          button,
          distanceElement,
        };
      });

      this.searchMatches = [...this.stores];
      this.visibleStores = [...this.stores];
      this.bindEvents();
      this.updateCount(this.stores.length);

      if (!this.stores.length) {
        this.showMapFallback('Nenhum ponto de venda disponível no momento.');
        return;
      }

      this.initializeMap();
    }

    bindEvents() {
      let searchTimer;
      this.searchInput?.addEventListener('input', () => {
        window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => this.filterStores(), 120);
      });

      this.stores.forEach((store) => {
        store.button?.addEventListener('click', () => this.focusStore(store, true));
      });

      this.locationButton?.addEventListener('click', () => this.requestCustomerLocation());
    }

    async initializeMap() {
      try {
        await this.waitForLeaflet();
        this.map = window.L.map(this.mapElement, {
          scrollWheelZoom: true,
          zoomControl: true,
        });

        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(this.map);

        this.markerLayer = window.L.layerGroup().addTo(this.map);
        this.createMarkers();
        this.updateVisibleMarkers(this.visibleStores);
        this.map.on('moveend', () => this.filterListByViewport());
        this.fitStores(this.visibleStores);
        this.filterListByViewport();

        window.setTimeout(() => this.map.invalidateSize(), 0);

        if (this.enableLocation && this.requestLocationOnLoad) {
          window.setTimeout(() => this.requestCustomerLocation(), 0);
        }
      } catch (_error) {
        this.showMapFallback('Não foi possível carregar o mapa. Consulte os pontos de venda na lista.');
      }
    }

    waitForLeaflet() {
      if (window.L) return Promise.resolve();

      return new Promise((resolve, reject) => {
        const leafletScript = document.querySelector('[data-store-locator-leaflet]');
        let attempts = 0;
        const interval = window.setInterval(() => {
          attempts += 1;
          if (window.L) {
            window.clearInterval(interval);
            resolve();
          } else if (attempts >= 80) {
            window.clearInterval(interval);
            reject(new Error('Leaflet indisponível'));
          }
        }, 100);

        leafletScript?.addEventListener(
          'error',
          () => {
            window.clearInterval(interval);
            reject(new Error('Falha ao carregar Leaflet'));
          },
          { once: true }
        );
      });
    }

    createMarkers() {
      const markerIcon = window.L.divIcon({
        className: 'store-locator__marker-wrap',
        html: `
          <span class="store-locator__marker" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" focusable="false">
              <path d="M5 10.5V19h14v-8.5" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path>
              <path d="M4 5h16l-1 5.5H5L4 5Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path>
              <path d="M9 19v-5h6v5" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path>
            </svg>
          </span>`,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
        popupAnchor: [0, -20],
      });

      this.stores.filter((store) => store.hasCoordinates).forEach((store) => {
        const marker = window.L.marker([store.latitude, store.longitude], {
          icon: markerIcon,
          keyboard: true,
          title: store.name,
          alt: `Ponto de venda ${store.name}`,
        });

        marker.bindPopup(this.createPopup(store));
        marker.on('click', () => this.setActiveStore(store));
        this.markers.set(store.id, marker);
      });
    }

    createPopup(store) {
      const popup = document.createElement('div');
      const name = document.createElement('strong');
      const address = document.createElement('p');
      const directions = document.createElement('a');

      name.className = 'store-locator__popup-name';
      name.textContent = store.name;
      address.className = 'store-locator__popup-address';
      address.textContent =
        store.city && !normalizeText(store.address).includes(normalizeText(store.city))
          ? `${store.address} — ${store.city}`
          : store.address;
      directions.className = 'store-locator__directions';
      directions.href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        `${store.latitude},${store.longitude}`
      )}`;
      directions.target = '_blank';
      directions.rel = 'noopener';
      directions.textContent = 'Como chegar';
      directions.setAttribute('aria-label', `Como chegar a ${store.name} (abre em uma nova janela)`);

      popup.append(name, address, directions);
      return popup;
    }

    updateVisibleMarkers(stores) {
      if (!this.markerLayer) return;
      this.markerLayer.clearLayers();
      stores.forEach((store) => {
        const marker = this.markers.get(store.id);
        if (marker) this.markerLayer.addLayer(marker);
      });
    }

    filterStores() {
      const query = normalizeText(this.searchInput?.value);
      const matches = query ? this.stores.filter((store) => store.searchText.includes(query)) : [...this.stores];
      this.searchMatches = matches;
      this.visibleStores = [...matches];
      this.renderStoreList(matches, 'Nenhum ponto de venda encontrado para esta busca.');
      this.updateVisibleMarkers(matches);

      if (!this.map || !matches.length) return;

      const mappedMatches = matches.filter((store) => store.hasCoordinates);
      if (!query && this.customerLocation) {
        this.map.setView([this.customerLocation.latitude, this.customerLocation.longitude], this.defaultZoom);
      } else if (mappedMatches.length === 1) {
        this.map.setView([mappedMatches[0].latitude, mappedMatches[0].longitude], this.defaultZoom);
      } else if (mappedMatches.length > 1) {
        this.fitStores(mappedMatches);
      }

      window.setTimeout(() => this.filterListByViewport(), 0);
    }

    filterListByViewport() {
      if (!this.map) return;

      const bounds = this.map.getBounds();
      const hasSearchQuery = normalizeText(this.searchInput?.value).length > 0;
      const mappedStoresInView = this.searchMatches.filter(
        (store) => store.hasCoordinates && bounds.contains([store.latitude, store.longitude])
      );
      const explicitlySearchedUnmappedStores = hasSearchQuery
        ? this.searchMatches.filter((store) => !store.hasCoordinates)
        : [];
      const storesInView = [...mappedStoresInView, ...explicitlySearchedUnmappedStores];

      this.visibleStores = storesInView;
      this.renderStoreList(
        storesInView,
        hasSearchQuery
          ? 'Nenhum ponto de venda visível nesta área para esta busca.'
          : 'Nenhum ponto de venda visível nesta área. Afaste o zoom ou mova o mapa.'
      );
    }

    renderStoreList(stores, emptyMessage) {
      const visibleStoreIds = new Set(stores.map((store) => store.id));

      this.stores.forEach((store) => {
        store.element.hidden = !visibleStoreIds.has(store.id);
      });

      if (this.noResults) {
        this.noResults.hidden = stores.length > 0;
        if (!stores.length) this.noResults.textContent = emptyMessage;
      }
      if (this.list) this.list.hidden = stores.length === 0;
      this.updateCount(stores.length);
    }

    fitStores(stores) {
      if (!this.map) return;
      const mappedStores = stores.filter((store) => store.hasCoordinates);

      if (mappedStores.length === 1) {
        this.map.setView([mappedStores[0].latitude, mappedStores[0].longitude], this.defaultZoom);
        return;
      }

      if (mappedStores.length > 1) {
        const bounds = window.L.latLngBounds(mappedStores.map((store) => [store.latitude, store.longitude]));
        this.map.fitBounds(bounds, { padding: [32, 32], maxZoom: this.defaultZoom });
        return;
      }

      this.showMapFallback('Nenhum ponto de venda possui uma localização válida no mapa.');
    }

    focusStore(store, openPopup) {
      if (!this.map || !store.hasCoordinates) return;
      const marker = this.markers.get(store.id);
      this.map.setView([store.latitude, store.longitude], this.defaultZoom);
      if (openPopup && marker) marker.openPopup();
      this.setActiveStore(store);
    }

    setActiveStore(activeStore) {
      this.stores.forEach((store) => {
        const isActive = store === activeStore;
        store.element.classList.toggle('is-active', isActive);
        if (isActive) {
          store.button?.setAttribute('aria-current', 'true');
        } else {
          store.button?.removeAttribute('aria-current');
        }
      });
    }

    requestCustomerLocation() {
      if (this.locationRequested || !this.enableLocation) return;
      this.locationRequested = true;

      if (!navigator.geolocation) {
        this.handleLocationError('Seu navegador não oferece suporte à localização.');
        return;
      }

      if (this.locationButton) this.locationButton.disabled = true;
      if (this.locationLabel) this.locationLabel.textContent = 'Obtendo sua localização…';
      this.setLocationStatus('Aguardando a permissão de localização do navegador.');

      navigator.geolocation.getCurrentPosition(
        (position) => this.handleLocationSuccess(position),
        (error) => {
          const messages = {
            1: 'Permissão de localização não concedida. Você ainda pode buscar uma loja normalmente.',
            2: 'Não foi possível determinar sua localização. Você ainda pode buscar uma loja normalmente.',
            3: 'A localização demorou demais para responder. Você ainda pode buscar uma loja normalmente.',
          };
          this.handleLocationError(messages[error.code] || 'Não foi possível usar sua localização.');
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    handleLocationSuccess(position) {
      this.customerLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      this.stores.forEach((store) => {
        if (!store.hasCoordinates) return;
        store.distance = haversineDistance(this.customerLocation, store);
        if (this.showDistance && store.distanceElement) {
          store.distanceElement.textContent = `${this.formatDistance(store.distance)} de você`;
          store.distanceElement.hidden = false;
        }
      });

      this.sortStoresByDistance();
      this.addCustomerMarker();
      this.map?.setView([this.customerLocation.latitude, this.customerLocation.longitude], this.defaultZoom);

      if (this.locationLabel) this.locationLabel.textContent = 'Localização encontrada';
      this.setLocationStatus('Lojas ordenadas da mais próxima para a mais distante.');
    }

    handleLocationError(message) {
      if (this.locationLabel) this.locationLabel.textContent = 'Localização indisponível';
      if (this.locationButton) this.locationButton.disabled = true;
      this.setLocationStatus(message);
    }

    addCustomerMarker() {
      if (!this.map || !this.customerLocation) return;

      if (this.customerMarker) this.customerMarker.remove();
      const icon = window.L.divIcon({
        className: 'store-locator__user-marker-wrap',
        html: '<span class="store-locator__user-marker" aria-hidden="true"></span>',
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      this.customerMarker = window.L.marker([this.customerLocation.latitude, this.customerLocation.longitude], {
        icon,
        keyboard: false,
        interactive: false,
      }).addTo(this.map);
    }

    sortStoresByDistance() {
      if (!this.list) return;
      const sortedStores = [...this.stores].sort((first, second) => {
        if (first.distance === null && second.distance === null) return first.originalIndex - second.originalIndex;
        if (first.distance === null) return 1;
        if (second.distance === null) return -1;
        return first.distance - second.distance;
      });

      sortedStores.forEach((store) => this.list.append(store.element));
    }

    formatDistance(distanceKm) {
      if (distanceKm < 1) return `${Math.round(distanceKm * 1000)} m`;
      const maximumFractionDigits = distanceKm < 10 ? 1 : 0;
      return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits }).format(distanceKm)} km`;
    }

    updateCount(count) {
      if (!this.resultsCount) return;
      this.resultsCount.textContent = `${count} ${count === 1 ? 'local' : 'locais'}`;
    }

    setLocationStatus(message) {
      if (this.locationStatus) this.locationStatus.textContent = message;
    }

    showMapFallback(message) {
      if (this.mapElement) this.mapElement.hidden = true;
      if (this.mapFallbackMessage) this.mapFallbackMessage.textContent = message;
      if (this.mapFallback) this.mapFallback.hidden = false;
    }
  }

  if (!customElements.get('store-locator')) {
    customElements.define('store-locator', StoreLocator);
  }
})();
